import {
  Box,
  Button,
  Image,
  Text,
  UnderMaintenance,
} from '@bonanza-social/moonlight';
import { memo, useMemo } from 'react';
import { ShopItemType } from '../../state/items/items.types';
import { useTranslation } from '@bonanza-social/i18n';
import ShopItemPrice from '../ShopItemPrice/ShopItemPrice';
import ShopBadgeList from '../badges/ShopBadgeList/ShopBadgeList';
import ShopItemInfo from '../ShopItemInfo/ShopItemInfo';
import { isAfterNow } from '@bonanza-social/toolkit/utils';
import usePurchaseShopItem from '../../hooks/usePurchaseShopItem';
import { openMaintenanceSheet } from '@bonanza-social/navigation';
import { selectIsTestAccount } from '@bonanza-social/account';
import { useSelector } from '@bonanza-social/toolkit/state';

function ShopItem(item: ShopItemType) {
  const {
    id,
    sale,
    name,
    thumbnail,
    availableFrom,
    maxWidth,
    type,
    isUnderMaintenance,
  } = item;

  const [t] = useTranslation('shop');

  const isComingSoon = useMemo(
    () => (availableFrom ? isAfterNow(availableFrom) : false),
    [availableFrom],
  );

  const { purchase, isPurchasing } = usePurchaseShopItem({
    id,
    type,
  });

  const isTestAccount = useSelector(selectIsTestAccount);
  const shouldShowMaintenance = isUnderMaintenance && !isTestAccount;

  const isSaleItemSoldOut =
    sale?.availableItems !== undefined && sale?.availableItems === 0;

  const itemOpacity = isSaleItemSoldOut ? 0.3 : 1;

  return (
    <Box
      maxWidth={maxWidth}
      flex={1}
      justifyContent="space-between"
      testID={`shop-item-${id}`}
    >
      <Box position="relative">
        <Image
          source={thumbnail}
          aspectRatio={1}
          borderRadius="radius-400"
          opacity={itemOpacity}
        />
        {shouldShowMaintenance ? (
          <UnderMaintenance
            onPress={() =>
              openMaintenanceSheet('shop', 'maintenanceDescription')
            }
          />
        ) : (
          <>
            <ShopItemInfo {...item} />
            {sale ? <ShopBadgeList sale={sale} /> : null}
          </>
        )}
      </Box>

      <Box gap="space-200" paddingTop="space-200" opacity={itemOpacity}>
        {type !== 'CashReward' ? (
          <Text
            variant="body-s-semi"
            color="darkSurfacePrimary"
            numberOfLines={2}
          >
            {name}
          </Text>
        ) : (
          <Box
            flexDirection="row"
            justifyContent="flex-start"
            alignItems="center"
            gap="space-200"
          >
            <Text
              variant="body-s-semi"
              color="darkSurfacePrimary"
              numberOfLines={2}
              maxWidth={70}
            >
              {name.includes('$') ? name : `$${name}`}
            </Text>
          </Box>
        )}

        <ShopItemPrice {...item} />

        {sale && sale.displayAvailableItems && sale.availableItems ? (
          <Text variant="caption" color="darkSurfaceMuted">
            {t('onlyXLeft', { itemsLeft: sale.availableItems })}
          </Text>
        ) : null}

        <Button
          locNS="shop"
          locKey={isComingSoon ? 'comingSoonCta' : 'redeemCta'}
          onPress={purchase}
          loading={isPurchasing}
          disabled={isPurchasing || isComingSoon || shouldShowMaintenance}
        />
      </Box>
    </Box>
  );
}

export default memo(ShopItem);
